import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, TemplateRef, inject, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { displayTraceId } from '@core/interceptors/trace-context';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModalRef, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';

export interface SpecialRequestsNotSavedData {
  /** the code the checkout answer carried (`specialRequests.errorCode`), REQUESTS_NOT_SAVED when it carried none */
  errorCode?: string | null;
  traceId?: string | null;
}

/**
 * R-SR12 (QA doc docs features/booking-special-requests/qa.md): the reservation was registered but its special requests were not
 * saved. The agent reads the code and the **Trace ID** (with **Copy**) and presses **OK**; the caller then opens the reservation page
 * as usual, where the requests can be added again. `role="alertdialog"` - the message is announced.
 */
@Component({
  selector: 'app-special-requests-not-saved-dialog',
  imports: [TranslatePipe, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="sr-ns" role="alertdialog" aria-labelledby="sr-ns-message" data-testid="special-requests-not-saved">
      <p id="sr-ns-message" class="sr-ns-text" data-testid="special-requests-not-saved-message">
        {{ 'Booking registered; special requests were not saved, tell the operator' | translate }}
      </p>
      <p class="sr-ns-meta">
        {{ 'Error code' | translate }}:
        <code data-testid="special-requests-not-saved-error-code">{{ code() }}</code>
      </p>
      @if (trace(); as id) {
        <p class="sr-ns-meta">
          {{ 'Trace ID' | translate }}:
          <code data-testid="special-requests-not-saved-trace-id">{{ id }}</code>
          <button
            type="button"
            class="sr-ns-copy"
            data-testid="special-requests-not-saved-trace-copy"
            [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
            (click)="copy(id)"
          >
            {{ (copied() ? 'Copied' : 'Copy') | translate }}
          </button>
        </p>
      }
    </div>
    <ng-template #modalFooterTemplateRef>
      <div class="sr-ns-footer">
        <button
          mat-flat-button
          color="primary"
          type="button"
          data-testid="special-requests-not-saved-ok"
          (click)="ok()"
        >
          {{ 'OK' | translate }}
        </button>
      </div>
    </ng-template>
  `,
  styles: `
    .sr-ns {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      color: var(--kz-color-text);
      font-size: 0.875rem;
      min-width: 0;
    }
    .sr-ns-text {
      margin: 0;
      font-weight: 600;
      overflow-wrap: anywhere;
    }
    .sr-ns-meta {
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      color: var(--kz-color-text-secondary);
    }
    .sr-ns-meta code {
      color: var(--kz-color-text);
      word-break: break-all;
    }
    .sr-ns-copy {
      border: 1px solid var(--kz-palette-light-blue-500);
      background: var(--c-ground);
      color: var(--kz-color-text);
      border-radius: 4px;
      padding: 0.0625rem 0.5rem;
      min-height: 1.75rem;
      cursor: pointer;
    }
    .sr-ns-copy:focus-visible {
      outline: 2px solid var(--kz-palette-light-blue-500);
      outline-offset: 2px;
    }
    .sr-ns-footer {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }
    @media (max-width: 599px) {
      .sr-ns-footer button {
        flex: 1 1 auto;
        min-height: 2.75rem;
      }
    }
  `
})
export class SpecialRequestsNotSavedDialogComponent implements OnDynamicModal<SpecialRequestsNotSavedData, 'ok'> {
  readonly #clipboard = inject(Clipboard);

  readonly code = signal('REQUESTS_NOT_SAVED');
  readonly trace = signal<string | null>(null);
  readonly copied = signal(false);

  readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');
  modalRef?: DynamicModalRef;
  onDynamicModalClose?: (result?: 'ok') => void;

  onDynamicModal(data: SpecialRequestsNotSavedData): void {
    this.code.set(data?.errorCode?.trim() ? data.errorCode : 'REQUESTS_NOT_SAVED');
    this.trace.set(displayTraceId(data?.traceId) ?? null);
  }

  copy(id: string): void {
    this.copied.set(this.#clipboard.copy(id));
  }

  ok(): void {
    this.onDynamicModalClose?.('ok');
  }
}
